/**
 * Tagebuchfotos auf dem Gerät: verkleinern, ablegen, hochladen.
 *
 * Der Weg eines Fotos hat drei Stationen, und keine darf es verlieren:
 *
 * 1. **Verkleinern im Browser** (`resizeToJpeg`) — 1600 px, JPEG, ohne EXIF.
 * 2. **Lokal ablegen** — Bild in `photo_blobs`, Beschreibung über die Outbox.
 * 3. **Hochladen** (`uploadPendingPhotos`) — **nur, wenn dieses Gerät es so
 *    eingestellt hat.** Vorgabe ist „nur auf diesem Gerät": Solange es keinen
 *    Server gibt, der durchläuft, ist das Telefon der verlässlichere Ort.
 *    Hochgeladen wird erst, wenn die Beschreibung beim Server ist; erst danach
 *    verschwindet das lokale Bild.
 *
 * Schlägt der Upload fehl, bleibt das Bild liegen und der Grund sichtbar.
 */

import {
  readAccountKey,
  readEncryptionSetup,
  openFile,
  sealFile,
  writeEncryptionSetup,
} from '../crypto/e2e.js';
import {
  fitWithin,
  newId,
  PHOTO_CONTENT_TYPE,
  PHOTO_MAX_BYTES,
  looksSealed,
  SEALED_FILE_CONTENT_TYPE,
} from '@kraftwerk/shared';
import { API_URL } from '../api.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalJournalPhoto, LocalPhotoBlob } from './local.js';
import { saveJournal } from './journal.js';
import { stamp, writeRow } from './write.js';

export interface PreparedPhoto {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Auf 1600 px verkleinern und als JPEG neu kodieren.
 *
 * `imageOrientation: 'from-image'` dreht Hochkant-Fotos richtig, bevor die
 * EXIF-Angabe beim Neukodieren wegfällt. Reicht die Qualitätsstufe nicht unter
 * 2 MB, wird weiter verkleinert — ein Foto, das der Server ablehnt, wäre
 * verloren, ohne dass es jemand merkt.
 */
export async function resizeToJpeg(file: Blob): Promise<PreparedPhoto> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  let { width, height } = fitWithin(bitmap.width, bitmap.height);

  try {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Kein Zeichenkontext');
      context.drawImage(bitmap, 0, 0, width, height);

      for (const quality of [0.85, 0.75, 0.65]) {
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, PHOTO_CONTENT_TYPE, quality),
        );
        if (blob && blob.size <= PHOTO_MAX_BYTES) return { blob, width, height };
      }
      width = Math.round(width * 0.8);
      height = Math.round(height * 0.8);
    }
  } finally {
    bitmap.close();
  }
  throw new Error('Foto ließ sich nicht unter 2 MB bringen');
}

/**
 * Ein Foto an den Tagebuchtag hängen. Gibt es den Tag noch nicht, entsteht er —
 * leer bis auf das Foto.
 *
 * Erst das Bild, dann die Beschreibung: Bricht dazwischen etwas ab, liegt ein
 * Bild ohne Beschreibung herum (harmlos), nie eine Beschreibung ohne Bild.
 */
export async function addPhoto(
  date: string,
  photo: PreparedPhoto,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalPhoto> {
  const entry = await saveJournal(date, {}, database);
  const id = newId();

  await database.photo_blobs.put({ id, blob: photo.blob, createdAt: stamp(), attempts: 0 });

  const row: LocalJournalPhoto = {
    id,
    updatedAt: stamp(),
    deletedAt: null,
    journalEntryId: entry.id,
    width: photo.width,
    height: photo.height,
    bytes: photo.blob.size,
    caption: null,
  };
  return writeRow('journal_photos', row, 'upsert', database);
}

export async function photosOf(
  journalEntryId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalPhoto[]> {
  if (!journalEntryId) return [];
  const rows = await database.journal_photos
    .where('journalEntryId')
    .equals(journalEntryId)
    .toArray();
  // Fotos eines Gedankens (E14) stehen beim Gedanken, nicht noch einmal am Tag.
  return rows
    .filter((row) => !row.deletedAt && !row.noteId)
    .sort((a, b) => a.id.localeCompare(b.id));
}

export async function localBlob(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<LocalPhotoBlob | null> {
  return (await database.photo_blobs.get(id)) ?? null;
}

export async function removePhoto(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.journal_photos.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'journal_photos',
    { ...existing, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
  // Ein gelöschtes Foto muss nicht mehr hoch.
  await database.photo_blobs.delete(id);
}

/** Adresse eines hochgeladenen Fotos. Die Sitzung reist als Cookie mit. */
export function photoUrl(id: string): string {
  return `${API_URL}/photos/${id}`;
}

/**
 * Ein hochgeladenes Bild als anzeigbarer Blob — verschlüsselte (E21) öffnet
 * das Gerät selbst. `locked`: verschlüsselt, das Gerät ist nicht entsperrt.
 */
export async function loadServerPhoto(
  id: string,
  database: KraftwerkDatabase = db,
  get: (url: string) => Promise<Response> = (url) => fetch(url, { credentials: 'include' }),
): Promise<Blob | 'locked' | null> {
  const response = await get(photoUrl(id)).catch(() => null);
  if (!response?.ok) return null;
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (!looksSealed(bytes)) return new Blob([bytes], { type: PHOTO_CONTENT_TYPE });
  const key = await readAccountKey(database);
  if (!key) return 'locked';
  try {
    const plain = (await openFile(bytes, key)) as Uint8Array<ArrayBuffer>;
    return new Blob([plain], { type: PHOTO_CONTENT_TYPE });
  } catch {
    return null;
  }
}

const PHOTOS_SEALED_KEY = 'e2ePhotosSealed';

/**
 * Nach dem Einrichten (E21): Bilder, die schon lesbar auf dem Server liegen,
 * einmal holen, verschlüsseln und ersetzen. Läuft nach jeder Abgleichrunde,
 * bis nichts Lesbares mehr übrig ist — dann merkt sich das Gerät, dass es
 * fertig ist. Scheitert ein Bild, bleibt es lesbar liegen und kommt beim
 * nächsten Mal wieder dran; verloren geht dabei nichts.
 */
export async function resealServerPhotos(
  database: KraftwerkDatabase = db,
  get: (url: string) => Promise<Response> = (url) => fetch(url, { credentials: 'include' }),
  put: PutPhoto = defaultPut,
): Promise<{ sealed: number; left: number }> {
  const key = await readAccountKey(database);
  if (!key || (await database.meta.get(PHOTOS_SEALED_KEY))?.value === 'ja') {
    return { sealed: 0, left: 0 };
  }
  const photos = await database.journal_photos
    .filter((photo) => Boolean(photo.uploadedAt) && !photo.deletedAt)
    .toArray();
  let sealed = 0;
  let left = 0;
  for (const photo of photos) {
    const response = await get(photoUrl(photo.id)).catch(() => null);
    if (!response?.ok) {
      left += 1;
      continue;
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (looksSealed(bytes)) continue;
    const body = new Blob([(await sealFile(bytes, key)) as Uint8Array<ArrayBuffer>], {
      type: SEALED_FILE_CONTENT_TYPE,
    });
    const { status } = await put(photo.id, body).catch(() => ({ status: 0 }));
    if (status >= 200 && status < 300) sealed += 1;
    else left += 1;
  }
  if (left === 0) await database.meta.put({ key: PHOTOS_SEALED_KEY, value: 'ja' });
  return { sealed, left };
}

export type PutPhoto = (id: string, blob: Blob) => Promise<{ status: number; reason?: string }>;

async function defaultPut(id: string, blob: Blob): Promise<{ status: number; reason?: string }> {
  const response = await fetch(photoUrl(id), {
    method: 'PUT',
    credentials: 'include',
    // Verschlüsselt (E21) mit eigenem Typ, sonst JPEG.
    headers: { 'Content-Type': blob.type || PHOTO_CONTENT_TYPE },
    body: blob,
  });
  if (response.status !== 413 && response.status !== 409) return { status: response.status };
  const body = (await response.json().catch(() => ({}))) as { reason?: string };
  return body.reason
    ? { status: response.status, reason: body.reason }
    : { status: response.status };
}

const QUOTA_FULL_KEY = 'photoQuotaFull';

/** Hat der Server zuletzt „Speicher voll" gemeldet? Dann warten Fotos auf dem Gerät. */
export async function readQuotaFull(database: KraftwerkDatabase = db): Promise<boolean> {
  return (await database.meta.get(QUOTA_FULL_KEY))?.value === 'ja';
}

/**
 * Ein Foto ins iOS-Teilen-Menü geben — von dort „In Dateien sichern" in einen
 * Ordner nach Wahl. Eine Web-App darf selbst in keinen sichtbaren Ordner
 * schreiben; das ist der Weg, den iOS offenlässt.
 *
 * Ohne Teilen-Funktion (Desktop) wird die Datei heruntergeladen.
 */
export async function savePhotoToFiles(
  blob: Blob,
  fileName: string,
): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const file = new File([blob], fileName, { type: PHOTO_CONTENT_TYPE });
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch {
      // Abgebrochen ist keine Fehlermeldung wert.
      return 'cancelled';
    }
  }
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
}

/** Ab so vielen echten Ablehnungen wird nicht weiter versucht — sichtbar, nicht still. */
export const MAX_PHOTO_ATTEMPTS = 5;

export interface PhotoUploadResult {
  uploaded: number;
  waiting: number;
  failed: number;
  /** Kontingent voll: Die übrigen Fotos bleiben auf dem Gerät, ohne als Fehlversuch zu zählen. */
  quotaFull?: boolean;
}

/**
 * Wartende Bilder hochladen.
 *
 * Nur, wenn die Beschreibung schon beim Server ist — steht sie noch in der
 * Outbox, antwortete der Server mit 404, und das wäre kein Fehler, sondern zu
 * früh. 404 trotz gesendeter Beschreibung zählt dagegen als Fehlversuch.
 */
export async function uploadPendingPhotos(
  database: KraftwerkDatabase = db,
  put: PutPhoto = defaultPut,
): Promise<PhotoUploadResult> {
  // Fotos gehen immer auf den Server (E21, 25.10.2026) — „nur auf dem Gerät“
  // gibt es nicht mehr. Lokal liegt ein Foto nur bis zum Hochladen.
  const blobs = await database.photo_blobs.toArray();
  // Ende-zu-Ende (E21): verschlüsselt hinaus; Konto verschlüsselt, Gerät nicht
  // entsperrt → warten, nie als lesbares Bild.
  const [key, sealedAccount] = await Promise.all([
    readAccountKey(database),
    readEncryptionSetup(database),
  ]);
  const queued = new Set(
    (await database.outbox.where('table').equals('journal_photos').toArray()).map(
      (entry) => entry.row.id,
    ),
  );

  const result: PhotoUploadResult = { uploaded: 0, waiting: 0, failed: 0 };

  for (const entry of blobs) {
    if (entry.uploadedAt) {
      // Schon oben: Kopie erst weg, wenn der Abgleich es bestätigt hat.
      const row = await database.journal_photos.get(entry.id);
      if (!row || row.deletedAt || row.uploadedAt) await database.photo_blobs.delete(entry.id);
      continue;
    }
    if (entry.attempts >= MAX_PHOTO_ATTEMPTS) {
      result.failed += 1;
      continue;
    }
    if (queued.has(entry.id)) {
      result.waiting += 1;
      continue;
    }

    if (!key && sealedAccount) {
      result.waiting += 1;
      continue;
    }

    let status: number;
    let reason: string | undefined;
    try {
      const body = key
        ? new Blob(
            [
              (await sealFile(
                new Uint8Array(await entry.blob.arrayBuffer()),
                key,
              )) as Uint8Array<ArrayBuffer>,
            ],
            { type: SEALED_FILE_CONTENT_TYPE },
          )
        : entry.blob;
      ({ status, reason } = await put(entry.id, body));
    } catch {
      // Kein Netz ist kein Fehlversuch — beim nächsten Mal wieder.
      result.waiting += 1;
      continue;
    }

    if (status >= 200 && status < 300) {
      await database.photo_blobs.update(entry.id, { uploadedAt: stamp() });
      await database.meta.put({ key: QUOTA_FULL_KEY, value: 'nein' });
      result.uploaded += 1;
    } else if (status === 409 && reason === 'encryption') {
      // Das Konto verschlüsselt inzwischen (anderes Gerät): kein Fehlversuch.
      await writeEncryptionSetup(true, database);
      result.waiting += 1;
    } else if (status === 413 && reason === 'quota') {
      // Speicher voll: kein Fehlversuch, kein weiteres Probieren in dieser Runde.
      // Das Foto ist sicher auf dem Gerät; die Einstellungen sagen, warum es dort bleibt.
      await database.meta.put({ key: QUOTA_FULL_KEY, value: 'ja' });
      result.quotaFull = true;
      result.waiting += 1;
      break;
    } else {
      await database.photo_blobs.update(entry.id, {
        attempts: entry.attempts + 1,
        lastError: `HTTP ${status}`,
      });
      result.failed += entry.attempts + 1 >= MAX_PHOTO_ATTEMPTS ? 1 : 0;
      result.waiting += entry.attempts + 1 >= MAX_PHOTO_ATTEMPTS ? 0 : 1;
    }
  }

  return result;
}

/** Wie viele Bilder noch auf dem Gerät auf das Hochladen warten. */
export async function pendingPhotoCount(database: KraftwerkDatabase = db): Promise<number> {
  return database.photo_blobs.filter((entry) => !entry.uploadedAt).count();
}
